import { useEffect, useRef, useState } from 'react';
import type { DigitalMenuProduct } from 'shared-models';
import { digitalMenuService } from '../../../services/digital-menu/digital-menu.service';

export function useFeaturedProductPicker(tenantId: string) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<DigitalMenuProduct[]>([]);
  const [known, setKnown] = useState<Record<number, DigitalMenuProduct>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const page = useRef(1);
  const lock = useRef(false);
  const request = useRef<AbortController | null>(null);
  const remember = (items: DigitalMenuProduct[]) => setKnown((current) => ({ ...current, ...Object.fromEntries(items.map((product) => [product.id, product])) }));

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setKnown({});
    digitalMenuService.profile(tenantId, controller.signal).then((profile) => {
      if (!controller.signal.aborted) remember(profile.featuredProducts ?? []);
    }).catch(() => {});
    return () => controller.abort();
  }, [tenantId]);

  const load = async (nextPage: number, controller: AbortController) => {
    if (lock.current) return;
    lock.current = true; setLoading(true); setError('');
    try {
      const result = await digitalMenuService.products(tenantId, nextPage, undefined, controller.signal, query);
      if (controller.signal.aborted) return;
      page.current = result.page;
      setProducts((current) => nextPage === 1 ? result.items : [...current, ...result.items]);
      remember(result.items); setHasMore(result.hasMore);
    } catch {
      if (!controller.signal.aborted) setError('לא ניתן לטעון מוצרים');
    } finally {
      if (!controller.signal.aborted) { lock.current = false; setLoading(false); }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    request.current = controller; lock.current = false; page.current = 1;
    setProducts([]); setHasMore(false);
    void load(1, controller);
    return () => { controller.abort(); lock.current = false; };
    // Each query owns its request and ignores results from the preceding query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, query, retry]);

  return { search, setSearch, products, known, loading, error, hasMore,
    retry: () => setRetry((value) => value + 1),
    loadMore: () => { if (hasMore && request.current) void load(page.current + 1, request.current); },
  };
}